/**
 * @file       Component file for login
 * @version    2.0.0
 * @author     Mehran Majidi
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or
 *             altered in anyway without expressed written permission from
 *             MindShare HDV LLC.
 */
import { Component, OnInit, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { SettingService } from '@app/service/setting.service';
import { AuthService } from '@app/service/auth.service';
import { HomepageService } from '@app/service/homepage.service';
import { PreferenceService } from '@app/service/preference.service';
import { SSO_MANUAL_PROMPT } from '@app/constants/Sso.constant';
import
{
    LOGOUT_MESSAGE,
    LOGOUT_QUERY_PARAM,
    LOGOUT_REASON
}
    from '@app/constants/Logout.constant';
import { validateUsername } from './username.validator';
import { validatePassword } from './password.validator';

@Component
({
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
class LoginComponent implements OnInit, AfterViewInit
{
    isSubmitted : boolean;
    loading : boolean;
    loginForm: FormGroup;
    usernameChanges: string;
    usernameStatus: string;
    username: FormControl;
    password : FormControl;
    public hidePassword: boolean = true;
    public showMicrosoftLogin: boolean;
    public isSessionMessage: boolean = false;
    private readonly autoSsoSessionKey = 'cypherAutoSsoAttempted';
//    returnUrl: string;
//    accessKey: string;
//    usernameParam: string;
    error = '';

    /**
     * @desc   - component constructor
     * @access - public
     * @param  -
     * @return -
     */
    constructor
    (
        private activatedRoute : ActivatedRoute,
        private router : Router,
        private settingService: SettingService,
        private authService: AuthService,
        private homepageService: HomepageService,
        private preferenceService: PreferenceService
    )
    {
        if (!this.authService.userValue)
        {
            localStorage.removeItem('activeInspectionNumber');
            localStorage.removeItem('caseUId');
        }
    }

    /**
     * @desc   - route after auth using settings, MFA, and homepage
     *           preference (not stale localStorage activePage).
     * @access - private
     * @param  -
     * @return -
     */
    private applyPostAuthRoutingFromLogin()
    {
        if (this.authService
            .userValue
            .mustChangePassword)
        {
            this.router.navigate(
                ['/settings']
            );
        }
        else if (
            this.settingService
                .isMultiFactorEnabled() &&
            !this.preferenceService
                .isTwoStepAuthDefined())
        {
            this.router.navigate(
                ['/settings']
            );
        }
        else if (this.authService.requiresVerification())
        {
            this.authService
                .setVerifiedStatus(false);
            this.router.navigate(
                ['/login/verify']
            );
        }
        else
        {
            this.authService
                .setVerifiedStatus(true);
            void this.homepageService
                .homepageProccessing(
                    this.authService
                        .userValue.uId
                );
        }
    }

    /**
     * @desc   - when login route loads with an active session, reload
     *           preferences before routing; pending MFA must not bypass
     *           verification by auto-entering the app
     * @access - private
     * @param  -
     * @return -
     */
    private async handleExistingSessionOnLogin()
    {
        let accessKeyParam: string;
        let hasUser: boolean;

        accessKeyParam =
            this.activatedRoute.snapshot.queryParams['accessKey'] || '';
        hasUser = this.authService.userValue ? true : false;

        if (!hasUser || accessKeyParam)
        {
            return;
        }

        await this.settingService.loadSettings(true);
        await this.preferenceService.loadPreferences();

        if (this.authService.requiresVerification())
        {
            this.authService.logout(
                LOGOUT_REASON.SESSION_EXPIRED
            );
        }
        else
        {
            this.applyPostAuthRoutingFromLogin();
        }
    }

    /**
     * @desc   - process login success
     * @access - private
     * @param  -
     * @return -
     */
    private processLoginSuccess()
    {
        this.applyPostAuthRoutingFromLogin();
        this.loading = false;
    }

        /**
     * @desc   - show Microsoft login from public SSO API (runtime / DB)
     * @access - private
     * @param  -
     * @return -
     */
    private loadMicrosoftLoginFromServer()
    {
        this.settingService.getPublicSsoEnabled()
        .subscribe(
        {
            next: (isEnabled: boolean) =>
            {
                this.showMicrosoftLogin = isEnabled === true;
            },
            error: () =>
            {
                this.showMicrosoftLogin = false;
            }
        });
    }

    /**
     * @desc   - map logout reason query param to login banner text
     * @access - private
     * @param  -
     * @return -
     */
    private resolveLogoutMessageFromQuery()
    {
        let reason: string;
        let message: string;

        reason =
            this.activatedRoute.snapshot
                .queryParams[LOGOUT_QUERY_PARAM] || '';
        message = '';

        if (reason === LOGOUT_REASON.IDLE)
        {
            message = LOGOUT_MESSAGE[LOGOUT_REASON.IDLE];
        }
        else if (reason === LOGOUT_REASON.SESSION_EXPIRED)
        {
            message =
                LOGOUT_MESSAGE[LOGOUT_REASON.SESSION_EXPIRED];
        }

        if (message)
        {
            this.error = message;
            this.isSessionMessage = true;
        }
    }

    /**
     * @desc   - angular initializer
     * @access - public
     * @param  -
     * @return -
     */
    ngOnInit()
    {
        let returnUrl;
        let accessKey;
        let username;
        let activeTab;
        let caseUId;
        let ssoLogin;
        let ssoError;

        // Hidden until GET setting/getPublicSsoEnabled returns (DB setting 32).
        // No environment/build flag; toggling SSO in settings applies on next
        // login page load without redeploying cypherWebApp.
        this.showMicrosoftLogin = false;
        this.error = '';
        this.isSessionMessage = false;
        this.loading = false;
        this.isSubmitted = false;
        this.loadMicrosoftLoginFromServer();
        this.resolveLogoutMessageFromQuery();

        // snapshot provides a snapshot of the query parameters, and if the URL
        // parameter changes in the comonent
        returnUrl =
            this.activatedRoute.snapshot.queryParams['returnUrl'] || '/';
        accessKey =
            this.activatedRoute.snapshot.queryParams['accessKey'] || '';
        username =
            this.activatedRoute.snapshot.queryParams['username'] || '';
        activeTab =
            this.activatedRoute.snapshot.queryParams['activeTab'] || '';
        caseUId =
            this.activatedRoute.snapshot.queryParams['caseUId'] || '';
        ssoLogin = this.activatedRoute.snapshot.queryParams['sso'] || '';
        ssoError = this.activatedRoute.snapshot.queryParams['error'] || '';

        if (accessKey)
        {
            if (this.authService.userValue)
            {
                this.authService.returnUrl = returnUrl;
                localStorage.setItem('returnUrl', returnUrl);
                localStorage.setItem('activeTab', activeTab);
                localStorage.setItem('caseUId', caseUId);
                void this.homepageService
                    .homepageProccessing(
                        this.authService.userValue.uId
                    );
            }
            else
            {


                // putting first, creates
                this.authService.verifyAccess(accessKey, username)
                .subscribe(data =>
                {
                    this.authService.returnUrl = returnUrl;
                    localStorage.setItem('returnUrl', returnUrl);
                    localStorage.setItem('activeTab', activeTab);
                    localStorage.setItem('caseUId', caseUId);
                    void this.homepageService
                        .homepageProccessing(
                            this.authService
                                .userValue.uId
                        );
                },
                error =>
                {
                    console.log(error);
                    //this.loginForm.get('username').updateValueAndValidity();
                    this.error = error;
                    //this.isSubmitted = false;
                });
            }
        }

        if (ssoLogin === '1')
        {
            this.loading = true;
            this.authService.completeSsoSessionLogin()
            .subscribe(
            {
                next: (response: any) =>
                {
                    if (response?.cyData?.returnUrl)
                    {
                        this.authService.returnUrl = response.cyData.returnUrl;
                        localStorage.setItem('returnUrl',
                                             response.cyData.returnUrl);
                    }
                    sessionStorage.removeItem(this.autoSsoSessionKey);
                    this.processLoginSuccess();
                },
                error: () =>
                {
                    this.loading = false;
                    this.error = 'SSO login failed.';
                }
            });
        }
        else if (ssoError)
        {
            this.error = 'SSO sign in is unavailable. Please login manually.';
        }

        this.username = new FormControl('', [Validators.required,
                                             validateUsername.bind(this)]);
        this.password = new FormControl('', [Validators.required,
                                             validatePassword]);
        this.loginForm = new FormGroup({
//            username : ['', [Validators.required]]
            username : this.username,
            password : this.password
//            username: new FormControl(username, [Validators.required]),
//            password: new FormControl()
        });

        this.isSubmitted = false;

        void this.handleExistingSessionOnLogin();
    }

    /**
     * @desc   - angular for after view is initialized
     * @access - public
     * @param  -
     * @return -
     */
    ngAfterViewInit()
    {
        this.loginForm.get('username')
            .valueChanges
            .subscribe(data =>
            {
                this.usernameChanges = data;
            }
        );
        this.loginForm.get('username')
            .statusChanges
            .subscribe(data =>
            {
                this.usernameStatus = data
            }
        );
    }

    /**
     * @desc   - when the login page is submiitted
     * @access - public
     * @param  -
     * @return -
     */
    onSubmit()
    {
        let usernameValue: string;
        let passwordValue: string;

        this.isSubmitted = true;
        this.isSessionMessage = false;
        this.error = '';
        this.username.markAsTouched();

        usernameValue = (this.username.value || '').trim();
        passwordValue = this.password.value || '';
        this.username.setValue(usernameValue);

        if (
            this.loginForm.invalid ||
            !usernameValue ||
            !passwordValue
        )
        {
            this.loading = false;
            this.error = 'Username and password are required';
            return;
        }

        this.loading = true;
        this.authService.login(
            usernameValue,
            passwordValue
        )
        .subscribe(
        {
            next: (data) =>
            {
                this.processLoginSuccess();
            },
            error: (error) =>
            {
                // Handle error, if needed
                this.loading = false;
                this.error = 'Invalid username or password';
            }
        })
    }

    /**
     * @desc   - start SSO login
     * @access - public
     * @param  - string promptValue : prompt value
     * @return -
     */
    onSsoLogin
    (
        promptValue: string = SSO_MANUAL_PROMPT
    )
    {
        let returnUrl;
        let startUrl;

        this.loading = true;
        returnUrl = this.activatedRoute.snapshot.queryParams['returnUrl'] || '';
        startUrl = this.authService.getSsoStartUrl(promptValue, returnUrl);
        if (startUrl)
        {
            window.location.href = startUrl;
        }
        else
        {
            this.loading = false;
            this.error = 'SSO sign in could not be started.';
        }
    }
}
export { LoginComponent };
